// === CONTENT-CONVERTER v1 ===
/**
 * RecallCore — Browser Content Converter
 * Converts a markdown/text source string into the requested format
 * and triggers a download via file-saver.
 *
 * Used by Path 2 (structured assets) format toggles.
 * Path 1 (real backend files) is untouched — it has its own download path.
 */

import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType
} from 'docx';
import pptxgen from 'pptxgenjs';

// ── Markdown stripping for plain-text formats ───────────────────────────────
export function stripMarkdown(md = '') {
  if (!md) return '';
  return String(md)
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '• ')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`{1,3}[^`]*`{1,3}/g, '')
    .replace(/^>\s+/gm, '')
    .replace(/^---+$/gm, '')
    .trim();
}

// ── HTML wrapper ────────────────────────────────────────────────────────────
function escapeHtml(s = '') {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function markdownToHtml(md = '', title = 'Document') {
  const lines = String(md).split('\n');
  const out = [];
  let inList = false;

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (/^#{1}\s+/.test(line)) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h1>${escapeHtml(line.replace(/^#\s+/, ''))}</h1>`);
    } else if (/^#{2}\s+/.test(line)) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h2>${escapeHtml(line.replace(/^##\s+/, ''))}</h2>`);
    } else if (/^#{3,6}\s+/.test(line)) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h3>${escapeHtml(line.replace(/^#{3,6}\s+/, ''))}</h3>`);
    } else if (/^[-*+]\s+/.test(line)) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${escapeHtml(line.replace(/^[-*+]\s+/, ''))}</li>`);
    } else if (line.trim() === '') {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push('');
    } else {
      if (inList) { out.push('</ul>'); inList = false; }
      let processed = escapeHtml(line);
      processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      processed = processed.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      out.push(`<p>${processed}</p>`);
    }
  }
  if (inList) out.push('</ul>');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${escapeHtml(title)}</title>
<style>
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a2e; line-height: 1.6; }
  h1 { color: #00a896; border-bottom: 2px solid #00a896; padding-bottom: 8px; margin-top: 32px; }
  h2 { color: #2dc896; margin-top: 24px; }
  h3 { color: #444; margin-top: 18px; }
  ul { padding-left: 24px; }
  li { margin: 4px 0; }
  p { margin: 8px 0; }
  strong { color: #1a1a2e; }
  hr { border: none; border-top: 1px solid #ddd; margin: 24px 0; }
</style>
</head>
<body>
${out.join('\n')}
</body>
</html>`;
}

// ── PDF generator (jspdf) ───────────────────────────────────────────────────
function buildPdf(md = '', title = 'Document') {
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 60;
  const marginY = 60;
  const usableWidth = pageWidth - (marginX * 2);
  let y = marginY;

  // Header bar
  doc.setFillColor(45, 200, 150);
  doc.rect(0, 0, pageWidth, 6, 'F');

  // Title
  doc.setTextColor(26, 26, 46);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(String(title).slice(0, 80), marginX, y + 18);
  y += 40;

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(`Produced by RecallCore · ${new Date().toLocaleDateString()}`, marginX, y);
  y += 20;

  // Divider
  doc.setDrawColor(45, 200, 150);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 18;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - marginY) {
      doc.addPage();
      y = marginY;
    }
  };

  const lines = String(md).split('\n');
  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.trim() === '') {
      y += 6;
      continue;
    }

    if (/^#\s+/.test(line)) {
      ensureSpace(28);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(26, 26, 46);
      const wrapped = doc.splitTextToSize(line.replace(/^#\s+/, ''), usableWidth);
      doc.text(wrapped, marginX, y);
      y += wrapped.length * 18 + 6;
    } else if (/^##\s+/.test(line)) {
      ensureSpace(22);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(45, 200, 150);
      const wrapped = doc.splitTextToSize(line.replace(/^##\s+/, ''), usableWidth);
      doc.text(wrapped, marginX, y);
      y += wrapped.length * 16 + 4;
    } else if (/^#{3,6}\s+/.test(line)) {
      ensureSpace(18);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(70, 70, 70);
      const wrapped = doc.splitTextToSize(line.replace(/^#{3,6}\s+/, ''), usableWidth);
      doc.text(wrapped, marginX, y);
      y += wrapped.length * 14 + 2;
    } else if (/^[-*+]\s+/.test(line)) {
      ensureSpace(14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      const text = line.replace(/^[-*+]\s+/, '');
      const wrapped = doc.splitTextToSize('• ' + text, usableWidth - 12);
      doc.text(wrapped, marginX + 6, y);
      y += wrapped.length * 12 + 2;
    } else if (/^---+$/.test(line.trim())) {
      ensureSpace(10);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.line(marginX, y, pageWidth - marginX, y);
      y += 10;
    } else {
      ensureSpace(14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      const cleaned = line.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1');
      const wrapped = doc.splitTextToSize(cleaned, usableWidth);
      doc.text(wrapped, marginX, y);
      y += wrapped.length * 12 + 4;
    }
  }

  // Footer on last page
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(170, 170, 170);
  doc.text('Produced by RecallCore.ai — Execution Infrastructure', pageWidth / 2, pageHeight - 30, { align: 'center' });

  return doc.output('blob');
}

// ── DOCX generator ──────────────────────────────────────────────────────────
async function buildDocx(md = '', title = 'Document') {
  const lines = String(md).split('\n');
  const children = [];

  children.push(new Paragraph({
    heading: HeadingLevel.HEADING_1,
    children: [new TextRun({ text: String(title), bold: true, size: 36, font: 'Arial' })]
  }));
  children.push(new Paragraph({
    children: [new TextRun({ text: `Produced by RecallCore · ${new Date().toLocaleDateString()}`, size: 18, color: '999999', font: 'Arial' })]
  }));
  children.push(new Paragraph({ children: [new TextRun('')] }));

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.trim() === '') {
      children.push(new Paragraph({ children: [new TextRun('')] }));
    } else if (/^#\s+/.test(line)) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: line.replace(/^#\s+/, ''), bold: true, size: 32, font: 'Arial' })]
      }));
    } else if (/^##\s+/.test(line)) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun({ text: line.replace(/^##\s+/, ''), bold: true, size: 26, color: '2DC896', font: 'Arial' })]
      }));
    } else if (/^#{3,6}\s+/.test(line)) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun({ text: line.replace(/^#{3,6}\s+/, ''), bold: true, size: 22, font: 'Arial' })]
      }));
    } else if (/^[-*+]\s+/.test(line)) {
      children.push(new Paragraph({
        bullet: { level: 0 },
        children: [new TextRun({ text: line.replace(/^[-*+]\s+/, ''), size: 22, font: 'Arial' })]
      }));
    } else if (/^---+$/.test(line.trim())) {
      children.push(new Paragraph({ children: [new TextRun({ text: '', size: 2 })] }));
    } else {
      const cleaned = line.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1');
      children.push(new Paragraph({
        children: [new TextRun({ text: cleaned, size: 22, font: 'Arial' })],
        spacing: { after: 120 }
      }));
    }
  }

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 }
        }
      },
      children
    }]
  });

  return await Packer.toBlob(doc);
}

// ── PPTX generator (pptxgenjs) ──────────────────────────────────────────────
async function buildPptx(md = '', title = 'Document') {
  const pres = new pptxgen();
  pres.layout = 'LAYOUT_WIDE';
  pres.title = String(title);
  pres.company = 'RecallCore.ai';

  const COLOR_NAVY  = '1A1A2E';
  const COLOR_TEAL_DEEP = '00A896';
  const COLOR_TEAL_BRIGHT = '2DC896';
  const COLOR_BODY  = '333333';
  const COLOR_MUTED = '888888';

  const cleanInline = (s = '') =>
    String(s)
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/[\u0060]([^\u0060]+)[\u0060]/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .trim();

  const lines = String(md).split('\n');
  const sections = [];
  let current = null;

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^#\s+/.test(line)) {
      if (current) sections.push(current);
      current = { title: cleanInline(line.replace(/^#\s+/, '')), blocks: [] };
    } else {
      if (!current) {
        current = { title: cleanInline(title), blocks: [] };
      }
      if (/^##\s+/.test(line)) {
        current.blocks.push({ type: 'subhead', text: cleanInline(line.replace(/^##\s+/, '')) });
      } else if (/^#{3,6}\s+/.test(line)) {
        current.blocks.push({ type: 'subhead', text: cleanInline(line.replace(/^#{3,6}\s+/, '')) });
      } else if (/^[-*+]\s+/.test(line)) {
        current.blocks.push({ type: 'bullet', text: cleanInline(line.replace(/^[-*+]\s+/, '')) });
      } else if (/^\d+\.\s+/.test(line)) {
        current.blocks.push({ type: 'bullet', text: cleanInline(line.replace(/^\d+\.\s+/, '')) });
      } else if (line.trim() === '') {
        current.blocks.push({ type: 'space' });
      } else {
        current.blocks.push({ type: 'para', text: cleanInline(line) });
      }
    }
  }
  if (current) sections.push(current);

  if (sections.length === 0) {
    sections.push({ title: cleanInline(title), blocks: [{ type: 'para', text: 'No content available.' }] });
  }

  const titleSlide = pres.addSlide();
  titleSlide.background = { color: 'FFFFFF' };
  titleSlide.addShape('rect', { x: 0, y: 0, w: 13.33, h: 0.15, fill: { color: COLOR_TEAL_DEEP } });
  titleSlide.addText(String(title), {
    x: 0.8, y: 2.6, w: 11.7, h: 1.8,
    fontSize: 44, bold: true, color: COLOR_NAVY, fontFace: 'Calibri', align: 'left', valign: 'middle',
  });
  titleSlide.addText('Produced by RecallCore.ai  ·  ' + new Date().toLocaleDateString(), {
    x: 0.8, y: 4.5, w: 11.7, h: 0.5,
    fontSize: 16, color: COLOR_TEAL_BRIGHT, fontFace: 'Calibri', align: 'left',
  });
  titleSlide.addText('RecallCore.ai  ·  Execution Infrastructure', {
    x: 0.8, y: 6.9, w: 11.7, h: 0.4,
    fontSize: 10, color: COLOR_MUTED, fontFace: 'Calibri', align: 'left',
  });

  const MAX_BLOCKS_PER_SLIDE = 12;
  for (const section of sections) {
    const blocks = section.blocks;
    const chunks = [];
    for (let i = 0; i < blocks.length; i += MAX_BLOCKS_PER_SLIDE) {
      chunks.push(blocks.slice(i, i + MAX_BLOCKS_PER_SLIDE));
    }
    if (chunks.length === 0) chunks.push([]);

    chunks.forEach((chunk, idx) => {
      const slide = pres.addSlide();
      slide.background = { color: 'FFFFFF' };

      const slideTitle = chunks.length > 1
        ? section.title + ' (' + (idx + 1) + '/' + chunks.length + ')'
        : section.title;
      slide.addText(slideTitle, {
        x: 0.6, y: 0.4, w: 12.1, h: 0.7,
        fontSize: 26, bold: true, color: COLOR_NAVY, fontFace: 'Calibri', align: 'left',
      });
      slide.addShape('rect', { x: 0.6, y: 1.05, w: 1.2, h: 0.05, fill: { color: COLOR_TEAL_BRIGHT } });

      const textRuns = [];
      for (const block of chunk) {
        if (block.type === 'subhead') {
          textRuns.push({ text: block.text, options: { bold: true, color: COLOR_TEAL_BRIGHT, fontSize: 16, breakLine: true } });
        } else if (block.type === 'bullet') {
          textRuns.push({ text: block.text, options: { bullet: true, color: COLOR_BODY, fontSize: 14, breakLine: true } });
        } else if (block.type === 'para') {
          textRuns.push({ text: block.text, options: { color: COLOR_BODY, fontSize: 14, breakLine: true } });
        } else if (block.type === 'space') {
          textRuns.push({ text: ' ', options: { fontSize: 8, breakLine: true } });
        }
      }

      if (textRuns.length === 0) {
        textRuns.push({ text: ' ', options: { color: COLOR_MUTED, fontSize: 12 } });
      }

      slide.addText(textRuns, {
        x: 0.6, y: 1.3, w: 12.1, h: 5.7,
        fontFace: 'Calibri', valign: 'top', align: 'left',
      });
    });
  }

  const closingSlide = pres.addSlide();
  closingSlide.background = { color: 'FFFFFF' };
  closingSlide.addShape('rect', { x: 0, y: 7.35, w: 13.33, h: 0.15, fill: { color: COLOR_TEAL_DEEP } });
  closingSlide.addText('Deployed via RecallCore.ai', {
    x: 0.8, y: 3.0, w: 11.7, h: 1.0,
    fontSize: 36, bold: true, color: COLOR_NAVY, fontFace: 'Calibri', align: 'center', valign: 'middle',
  });
  closingSlide.addText('Your On-Demand Execution Team', {
    x: 0.8, y: 4.0, w: 11.7, h: 0.6,
    fontSize: 18, color: COLOR_TEAL_BRIGHT, fontFace: 'Calibri', align: 'center', italic: true,
  });

  const blob = await pres.write({ outputType: 'blob' });
  return blob;
}


// ── Public API: convertAndDownload ──────────────────────────────────────────
/**
 * @param {object} asset - { content, filename, format, mimeType, ... }
 * @param {string} targetFormat - 'md' | 'txt' | 'html' | 'pdf' | 'docx' | 'csv' | 'json'
 *
 * baseName is derived from asset.filename (stripping the original extension).
 * Returns a Promise. Triggers the download via file-saver.
 */
export async function convertAndDownload(asset, targetFormat) {
  const sourceContent = String(asset?.content || '');
  const sourceFormat = String(asset?.format || '').toLowerCase();
  const fmt = String(targetFormat || sourceFormat || 'md').toLowerCase();
  const filename = String(asset?.filename || 'document');
  const baseName = filename.replace(/\.[^./\\]+$/, '');
  const title = baseName.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // If user picked the same format the asset already is, ship the original content directly
  if (fmt === sourceFormat) {
    let mime = asset?.mimeType || 'text/plain;charset=utf-8';
    let body = sourceContent;
    if (fmt === 'csv') {
      mime = 'text/csv;charset=utf-8';
      // BOM is already included from G1 source patch — but defensive: prepend if missing
      if (!body.startsWith('\uFEFF')) body = '\uFEFF' + body;
    }
    if (fmt === 'json') mime = 'application/json;charset=utf-8';
    const blob = new Blob([body], { type: mime });
    saveAs(blob, `${baseName}.${fmt}`);
    return;
  }

  // Source is structured (json/csv) and target is text-ish — use raw content as the text source
  // The downstream conversions treat sourceContent as markdown-ish; non-markdown sources still convert reasonably.
  switch (fmt) {
    case 'md': {
      const blob = new Blob([sourceContent], { type: 'text/markdown;charset=utf-8' });
      saveAs(blob, `${baseName}.md`);
      return;
    }
    case 'txt': {
      const text = stripMarkdown(sourceContent);
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${baseName}.txt`);
      return;
    }
    case 'html': {
      const html = markdownToHtml(sourceContent, title);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      saveAs(blob, `${baseName}.html`);
      return;
    }
    case 'pdf': {
      const blob = buildPdf(sourceContent, title);
      saveAs(blob, `${baseName}.pdf`);
      return;
    }
    case 'docx': {
      const blob = await buildDocx(sourceContent, title);
      saveAs(blob, `${baseName}.docx`);
      return;
    }
    case 'csv': {
      // Cross-format conversion to CSV doesn't make sense for prose markdown
      // Fall through to original content with BOM
      let body = sourceContent;
      if (!body.startsWith('\uFEFF')) body = '\uFEFF' + body;
      const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `${baseName}.csv`);
      return;
    }
    case 'json': {
      const blob = new Blob([sourceContent], { type: 'application/json;charset=utf-8' });
      saveAs(blob, `${baseName}.json`);
      return;
    }
    case 'pptx': {
      const blob = await buildPptx(sourceContent, title);
      saveAs(blob, `${baseName}.pptx`);
      return;
    }
    default: {
      const blob = new Blob([sourceContent], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${baseName}.${fmt}`);
    }
  }
}

// ── Helper: which target formats are available for a given asset format ────
export function availableTargetFormats(sourceFormat) {
  const src = String(sourceFormat || 'md').toLowerCase();
  if (src === 'csv') return ['csv'];
  if (src === 'json') return ['json'];
  // Markdown / md / txt sources convert into the deliverable family
  return ['md', 'docx', 'pdf', 'pptx'];
}
